import React, { useState, useEffect } from 'react';
import { CreditCard, Percent, Calendar, CheckCircle, AlertCircle, Calculator, CreditCard as CreditCardIcon } from 'lucide-react';
import { paymentOptionsAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const PaymentOptions = ({ onPaymentOptionChange, selectedPaymentOption, appliedCoupon, onCouponChange, totalAmount, formatPrice, onConfigLoaded, onEmiDetailsChange, productId, disableFullPaymentCoupon }) => {
  const { notify } = useNotificationHook();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [selectedEmiTenure, setSelectedEmiTenure] = useState('');
  const [emiAmount, setEmiAmount] = useState(0);
  const [totalEmiAmount, setTotalEmiAmount] = useState(0);

  useEffect(() => {
    fetchPaymentOptionsConfig();
  }, []);

  // Calculate EMI when tenure changes
  useEffect(() => {
    if (selectedPaymentOption === 'emi' && selectedEmiTenure && totalAmount && config?.emi_interest_rate) {
      const emiPrincipal = getEmiPrincipal(); // Use remaining amount after advance
      const principal = emiPrincipal + parseFloat(config?.emi_processing_fee || 0);
      // For flat rate method, we use the annual rate directly (not monthly)
      const emi = calculateEMI(principal, parseFloat(config.emi_interest_rate), parseInt(selectedEmiTenure));
      setEmiAmount(emi);
      setTotalEmiAmount(calculateTotalEMIAmount(emi, parseInt(selectedEmiTenure)));
    }
  }, [selectedEmiTenure, totalAmount, config?.emi_interest_rate, config?.emi_processing_fee, config?.emi_advance_percentage, selectedPaymentOption]);

  // Pass EMI details to parent component
  useEffect(() => {
    if (onEmiDetailsChange && selectedPaymentOption === 'emi') {
      const emiDetails = {
        selectedEmiTenure,
        emiAmount,
        totalEmiAmount,
        emiPrincipal: getEmiPrincipal(),
        emiAdvanceAmount: getEmiAdvanceAmount()
      };
      onEmiDetailsChange(emiDetails);
    }
  }, [selectedEmiTenure, emiAmount, totalEmiAmount, selectedPaymentOption]);

  const fetchPaymentOptionsConfig = async () => {
    try {
      setLoading(true);
      const response = await paymentOptionsAPI.getConfig();
      
      if (response?.data?.success) {
        const configData = {};
        Object.keys(response.data.data).forEach(key => {
          configData[key] = response.data.data[key].value;
        });
        setConfig(configData);
        
        // Pass config to parent component
        if (onConfigLoaded) {
          onConfigLoaded(configData);
        }
      }
    } catch (error) {
      console.error('Error fetching payment options config:', error);
      notify.error('Failed to load payment options configuration', 'Configuration Error');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentOptionSelect = (option) => {
    onPaymentOptionChange(option);
    
    // Clear coupon if switching to split payment or EMI
    if ((option === 'advance' || option === 'emi') && appliedCoupon) {
      onCouponChange(null);
      setCouponCode('');
      setCouponError('');
    }

    // Reset EMI selection if switching away from EMI
    if (option !== 'emi') {
      setSelectedEmiTenure('');
      setEmiAmount(0);
      setTotalEmiAmount(0);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (selectedPaymentOption !== 'full') {
      setCouponError('Coupon codes are only available for full payment');
      return;
    }

    try {
      setValidatingCoupon(true);
      setCouponError('');
      
      const response = await paymentOptionsAPI.validateCoupon(couponCode, 'full', productId);
      
      if (response?.data?.success && response.data.data.valid) {
        const couponData = {
          code: response.data.data.coupon_code,
          discount_percentage: response.data.data.discount_percentage,
          title: 'Full Payment Discount',
          description: response.data.data.message,
          type: 'full_payment'
        };
        
        onCouponChange(couponData);
        setCouponCode('');
        notify.success('Coupon applied successfully!', 'Coupon Applied');
      } else {
        setCouponError(response.data.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('Failed to validate coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponChange(null);
    setCouponCode('');
    setCouponError('');
  };

  // EMI calculation functions
  // Calculate EMI amount using Flat Rate method
  const calculateEMI = (principal, rate, tenure) => {
    if (!principal || !rate || !tenure) return 0;
    
    // Flat Rate Method: Interest is calculated on the entire principal for the entire tenure
    const totalInterest = (principal * rate * tenure) / 100; // Total interest for entire tenure
    const totalAmount = principal + totalInterest; // Principal + Total Interest
    const monthlyEMI = totalAmount / tenure; // Equal monthly installments
    
    return monthlyEMI;
  };

  const calculateTotalEMIAmount = (emiAmount, tenure) => {
    if (!emiAmount || !tenure) return 0;
    return emiAmount * tenure;
  };

  // Get available EMI tenures
  const getEmiTenures = () => {
    if (!config?.emi_tenure_months) return [3, 6, 9, 12];
    return config.emi_tenure_months.split(',').map(t => parseInt(t.trim())).filter(t => !isNaN(t));
  };

  // Check EMI eligibility
  const isEmiEligible = () => {
    if (!totalAmount || !config?.emi_minimum_amount) return false;
    return totalAmount >= parseFloat(config.emi_minimum_amount);
  };

  // Calculate EMI advance amount
  const getEmiAdvanceAmount = () => {
    const advancePercentage = config?.emi_advance_percentage || 50;
    return (totalAmount * advancePercentage) / 100;
  };

  // Calculate EMI principal (remaining amount after advance)
  const getEmiPrincipal = () => {
    return totalAmount - getEmiAdvanceAmount();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config?.payment_options_enabled) {
    return null;
  }





  // Calculate split payment amount
  const advancePaymentAmount = totalAmount ? (totalAmount * (config.advance_payment_percentage || 50)) / 100 : 0;
  const afterDemoAmount = totalAmount ? (totalAmount * (config.advance_payment_after_demo_percentage || 25)) / 100 : 0;
  const afterProjectAmount = totalAmount ? (totalAmount * (config.advance_payment_after_project_percentage || 25)) / 100 : 0;

  return (
    <div className="space-y-6">
      {/* Payment Options Title */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {config.payment_options_title || 'Choose Your Payment Option'}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {config.payment_options_description || 'Select your preferred payment method and schedule'}
        </p>
      </div>

      {/* Payment Options */}
      <div className="space-y-4">
        {/* Full Payment and Split Payment in a row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Payment Option */}
        {config.full_payment_enabled && (
          <div
            className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
              selectedPaymentOption === 'full'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
            onClick={() => handlePaymentOptionSelect('full')}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${
                  selectedPaymentOption === 'full' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Full Payment</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pay the complete amount upfront</p>
                </div>
              </div>
              {selectedPaymentOption === 'full' && (
                <CheckCircle className="w-6 h-6 text-blue-500" />
              )}
            </div>

            {/* Coupon Section for Full Payment */}
            {selectedPaymentOption === 'full' && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center space-x-2 mb-3">
                  <Percent className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">
                    {disableFullPaymentCoupon ? 'Special Discount Unavailable' : 'Special Discount Available'}
                  </span>
                </div>
                
                {disableFullPaymentCoupon ? (
                  <div className="space-y-2">
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Full Payment Special Discount Coupon code will not work for this product.
                    </p>
                  </div>
                ) : !appliedCoupon ? (
                  <div className="space-y-3">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {config.full_payment_coupon_message || 'Use coupon code FUL20 to get 20% discount on full payment'}
                    </p>
                    <div className="relative">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter FUL20"
                        className="w-full pr-28 pl-3 py-3 text-sm border border-green-300 dark:border-green-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        maxLength={10}
                      />
                      <button
                        onClick={handleApplyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="absolute right-1 top-1 bottom-1 px-4 text-sm bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors"
                      >
                        {validatingCoupon ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                        <AlertCircle className="w-4 h-4" />
                        <span>{couponError}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 dark:text-green-300">
                        Coupon applied: {appliedCoupon.code} ({appliedCoupon.discount_percentage}% off)
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-sm text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Split Payment Option */}
        {config.advance_payment_enabled && (
          <div
            className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
              selectedPaymentOption === 'advance'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
            onClick={() => handlePaymentOptionSelect('advance')}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${
                  selectedPaymentOption === 'advance' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Split Payment</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pay in installments</p>
                </div>
              </div>
              {selectedPaymentOption === 'advance' && (
                <CheckCircle className="w-6 h-6 text-blue-500" />
              )}
            </div>

            {/* Payment Schedule for Split Payment */}
            {selectedPaymentOption === 'advance' && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Payment Schedule
                  </span>
                </div>
                
                {/* Current Payment Amount */}
                <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-800 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Amount to Pay Now:</span>
                    <span className="text-lg font-bold text-blue-900 dark:text-blue-100">
                      {formatPrice ? formatPrice(advancePaymentAmount) : `₹${advancePaymentAmount.toFixed(2)}`}
                    </span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    This is {config.advance_payment_percentage || 50}% of the total amount ({formatPrice ? formatPrice(totalAmount) : `₹${totalAmount?.toFixed(2)}`})
                  </p>
                </div>
                
                {/* Payment Schedule */}
                <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                  <div className="flex justify-between">
                    <span>{config.advance_payment_now_text || 'Split (Now)'}:</span>
                    <span className="font-medium">{formatPrice ? formatPrice(advancePaymentAmount) : `₹${advancePaymentAmount.toFixed(2)}`} ({config.advance_payment_percentage || 50}%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{config.advance_payment_after_demo_text || 'After Demo Completion'}:</span>
                    <span className="font-medium">{formatPrice ? formatPrice(afterDemoAmount) : `₹${afterDemoAmount.toFixed(2)}`} ({config.advance_payment_after_demo_percentage || 25}%)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{config.advance_payment_after_project_text || 'After Project Completion'}:</span>
                    <span className="font-medium">{formatPrice ? formatPrice(afterProjectAmount) : `₹${afterProjectAmount.toFixed(2)}`} ({config.advance_payment_after_project_percentage || 25}%)</span>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800 rounded-md">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    {config.advance_payment_instructions || 'Split: 50% | After Demo Completion: 25% | After Project Completion: 25%'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* EMI Payment Option */}
        {config.emi_payment_enabled && (
          <div
            className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
              selectedPaymentOption === 'emi'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            } ${!isEmiEligible() ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => isEmiEligible() && handlePaymentOptionSelect('emi')}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${
                  selectedPaymentOption === 'emi' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  <CreditCardIcon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">EMI Payment</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pay in monthly installments</p>
                </div>
              </div>
              {selectedPaymentOption === 'emi' && (
                <CheckCircle className="w-6 h-6 text-blue-500" />
              )}
            </div>

            {/* EMI Eligibility Message */}
            {!isEmiEligible() && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {config.emi_eligibility_message || `EMI available on orders above ₹${config.emi_minimum_amount || 1000}`}
                </p>
              </div>
            )}

            {/* EMI Calculator for Selected Option */}
            {selectedPaymentOption === 'emi' && isEmiEligible() && (
              <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center space-x-2 mb-3">
                  <Calculator className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                    {config.emi_calculator_title || 'EMI Calculator'}
                  </span>
                </div>
                
                <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                  {config.emi_calculator_description || 'Calculate your monthly EMI amount'} (Flat Rate Method)
                </p>

                {/* Tenure Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                    Select Tenure:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {getEmiTenures().map((tenure) => (
                      <button
                        key={tenure}
                        onClick={() => setSelectedEmiTenure(tenure.toString())}
                        className={`p-2 text-sm rounded-md border transition-colors ${
                          selectedEmiTenure === tenure.toString()
                            ? 'border-purple-500 bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200'
                            : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600'
                        }`}
                      >
                        {tenure} Months
                      </button>
                    ))}
                  </div>
                </div>

                {/* EMI Calculation Results */}
                {selectedEmiTenure && emiAmount > 0 && (
                  <div className="space-y-3">
                    {/* EMI Advance Information */}
                    <div className="p-3 bg-blue-100 dark:bg-blue-800 rounded-md">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Split Payment:</span>
                        <span className="text-lg font-bold text-blue-900 dark:text-blue-100">
                          {formatPrice ? formatPrice(getEmiAdvanceAmount()) : `₹${getEmiAdvanceAmount().toFixed(2)}`}
                        </span>
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {config.emi_advance_message || 'Pay 50% advance now, remaining amount in EMI'}
                      </p>
                    </div>

                    <div className="p-3 bg-purple-100 dark:bg-purple-800 rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-purple-800 dark:text-purple-200">Monthly EMI:</span>
                        <span className="text-lg font-bold text-purple-900 dark:text-purple-100">
                          {formatPrice ? formatPrice(emiAmount) : `₹${emiAmount.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
                        <div className="flex justify-between">
                          <span>Total Order Amount:</span>
                          <span>{formatPrice ? formatPrice(totalAmount) : `₹${totalAmount?.toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Advance Amount:</span>
                          <span>{formatPrice ? formatPrice(getEmiAdvanceAmount()) : `₹${getEmiAdvanceAmount().toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>EMI Principal:</span>
                          <span>{formatPrice ? formatPrice(getEmiPrincipal()) : `₹${getEmiPrincipal().toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Processing Fee:</span>
                          <span>{formatPrice ? formatPrice(parseFloat(config.emi_processing_fee || 0)) : `₹${parseFloat(config.emi_processing_fee || 0).toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Interest Rate:</span>
                          <span>{config.emi_interest_rate || 15}% p.a. (Flat Rate)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total EMI Amount:</span>
                          <span className="font-medium">{formatPrice ? formatPrice(totalEmiAmount) : `₹${totalEmiAmount.toFixed(2)}`}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bank Partners */}
                    {config.emi_bank_partners && (
                      <div className="text-xs text-purple-700 dark:text-purple-300">
                        <span className="font-medium">Partner Banks:</span> {config.emi_bank_partners}
                      </div>
                    )}

                    {/* Terms and Conditions */}
                    <div className="text-xs text-purple-600 dark:text-purple-400">
                      {config.emi_terms_conditions || 'EMI is subject to bank approval. Processing fee applies.'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* No Payment Options Available */}
      {(!config.full_payment_enabled && !config.advance_payment_enabled && !config.emi_payment_enabled) && (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Payment Options Available
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Please contact our support team for payment assistance.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentOptions;
